import { useLocation, useNavigate, useParams, Navigate } from 'react-router-dom';
import { NotFound } from '../NotFound';
import type { FeatureId } from '@sequesto-os/shared';
import { useAccess } from '../../hooks/useAccess';
import { TabBar, type TabItem } from '../../components/ui/Tabs';
import { PageHeader, PageShell } from '../../components/ui/PageHeader';
import { OverviewView } from '../Dashboard/gtm';
import { ContentDashboard } from '../Automations/ContentDashboard';
import { VisibilityView } from './VisibilityView';
import { WebsiteView } from './WebsiteView';
import { VisualsView } from './VisualsView';
import { RANGE_PARAM } from './shared';

/**
 * Marketing in five tabs:
 * - Overview   — the inbound board (was "GTM") with performance marketing below it
 * - Visibility — Google (Semrush) and AI assistants (Peec) behind one toggle
 * - Website    — first-party tracking + GA4 + RB2B on one shared period
 * - Content    — the content engine: Website / LinkedIn / Opportunities / Rules
 * - Visuals    — Lea's SEQUESTO Live kit: social visuals and short MP4s
 *
 * Merged tabs keep the feature ids of their parts, so access is unchanged: a
 * tab shows when any of its parts is allowed, and each part checks its own id.
 */
type Section = 'overview' | 'visibility' | 'website' | 'content' | 'visuals';

const SECTIONS: readonly TabItem<Section>[] = [
  { id: 'overview', label: 'Overview' },
  { id: 'visibility', label: 'Visibility' },
  { id: 'website', label: 'Website' },
  { id: 'content', label: 'Content' },
  { id: 'visuals', label: 'Visuals' },
];

const SECTION_FEATURES: Record<Section, readonly FeatureId[]> = {
  overview: ['marketing.gtm'],
  visibility: ['marketing.search-visibility', 'marketing.visibility'],
  // The data endpoints behind this tab are gated on marketing.web-analytics.
  website: ['marketing.web-analytics'],
  content: ['marketing.content'],
  visuals: ['marketing.visuals'],
};

/** Old tab URLs → their new home. `gtm` also reads its old `?tab=`. */
function legacyTarget(section: string, search: URLSearchParams): string | null {
  switch (section) {
    case 'search-visibility':
      return '/marketing/visibility';
    case 'website-visits':
    case 'web-analytics':
      return '/marketing/website';
    case 'gtm': {
      const tab = search.get('tab');
      if (tab === 'content' || tab === 'calendar') return '/marketing/content/opportunities';
      if (tab === 'health') return '/marketing/visibility';
      const kept = new URLSearchParams(search);
      kept.delete('tab');
      const qs = kept.toString();
      return `/marketing/overview${qs ? `?${qs}` : ''}`;
    }
    default:
      return null;
  }
}

function isSection(value: string | undefined): value is Section {
  return SECTIONS.some((s) => s.id === value);
}

export function Marketing() {
  // The content engine carries extra path segments (website/linkedin/...), so the
  // section is the first param and the rest is read by ContentDashboard itself.
  const { section } = useParams<{ section?: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { canAccess } = useAccess();

  const allowed = (s: Section) => SECTION_FEATURES[s].some((f) => canAccess(f));
  const visibleSections = SECTIONS.filter((s) => allowed(s.id));
  const firstSection = visibleSections[0]?.id;

  const legacy = section ? legacyTarget(section, new URLSearchParams(location.search)) : null;
  if (legacy) return <Navigate to={legacy} replace />;

  if (section && !isSection(section)) return <NotFound />;
  if (!section || !isSection(section) || !allowed(section)) {
    return firstSection ? <Navigate to={`/marketing/${firstSection}`} replace /> : <Navigate to="/" replace />;
  }

  // The period is shared by every tab, so it rides along when the tab changes.
  const range = new URLSearchParams(location.search).get(RANGE_PARAM);
  const goTo = (id: Section) => navigate(`/marketing/${id}${range ? `?${RANGE_PARAM}=${encodeURIComponent(range)}` : ''}`);

  return (
    <PageShell>
      <PageHeader eyebrow="Departments" title="Marketing">
        {visibleSections.length > 1 && (
          <TabBar tabs={visibleSections} active={section} onSelect={goTo} size="md" />
        )}
      </PageHeader>

      {section === 'overview' && <OverviewView />}
      {section === 'visibility' && (
        <VisibilityView
          canGoogle={canAccess('marketing.search-visibility')}
          canAi={canAccess('marketing.visibility')}
        />
      )}
      {section === 'website' && <WebsiteView />}
      {section === 'content' && <ContentDashboard />}
      {section === 'visuals' && <VisualsView />}
    </PageShell>
  );
}
